import * as moment from "moment";
import DateUtils from "../../utils/DateUtils";
import LeadStatusNotification from "./lead/LeadStatusNotification";
import ReminderNotification from "./reminder/ReminderNotification";
import LeadWatcherChangeNotification from "./watcher/LeadWatcherChangeNotification";
import AssigneeChangeNotification from "./assignee/AssigneeChangeNotification";
import SmsNotification from "./sms/SmsNotification";
import QuoteCommentNotification from "./quote/QuoteCommentNotification";
import QuoteStatusChangedNotification from "./quote/QuoteStatusChangedNotification";
import UserMentionNotification from "./mention/UserMentionNotification";
import JobUserMentionNotification from "./mention/JobUserMentionNotification";
import JobReminderNotification from "./reminder/JobReminderNotification";
import JobStatusNotification from "./job/JobStatusNotification";
import LeadBDPChangeNotification from "./assignee/LeadBDPChangeNotification";
import JobBDPChangeNotification from "./assignee/job/JobBDPChangeNotification";
import JobAgentChangeNotification from "./assignee/job/JobAgentChangeNotification";
import LeadAgentChangeNotification from "./assignee/LeadAgentChangeNotification";
import JobWatcherChangeNotification from "./watcher/JobWatcherChangeNotification";
import JobCreatedFromQuoteNotification from "./job/JobCreatedFromQuoteNotification";
import NewThirdPartyLeadNotification from "./thirdPartyLead/NewThirdPartyLeadNotification";
import InvoiceCreatedNotification from "./invoice/InvoiceCreatedNotification";
import BillCreatedNotification from "./invoice/BillCreatedNotification";
import PaymentArrivedNotification from "./payment/PaymentArrivedNotification";
import BillPaidNotification from "./invoice/BillPaidNotification";

/**
 * Base notification class.
 */
export default abstract class NotificationBase {
	/**
	 * @param {string} id Notification id.
	 * @param {moment.Moment} visibleFrom Notification datetime.
	 * @param {boolean} read Flag if note is marked as read.
	 * @param {string} type Notification type.
	 */
	protected constructor(readonly id: string,
		readonly visibleFrom: moment.Moment,
		readonly read: boolean,
		readonly type: string) {
	}

	/**
	 * Display date as formatted string.
	 * @returns {string}
	 */
	displayDate(): string {
		return this.visibleFrom.format(DateUtils.DATE_TIME_FORMAT)
	}

	setReadStatus(read: boolean): NotificationBase {
		return this.copy({read})
	}

	abstract copy(p: { read: boolean }): NotificationBase

	public static compare(a: NotificationBase, b: NotificationBase) {
		return a.visibleFrom.diff(b.visibleFrom);
	}
}

/**
 * Notifications types enum.
 */
export enum NotificationTypeEnum {
	Note = "LEAD_NOTE_REMINDER",
	LeadStatus = "LEAD_STATUS",
	LeadAssignees = "LEAD_ASSIGNEES",
	LeadBDPAssignee = "LEAD_BUSINESS_DEVELOPMENT_PARTNER_ASSIGNED",
	LeadAgentAssignee = "LEAD_AGENT_ASSIGNED",
	LeadWatchers = "LEAD_WATCHERS",
	NewThirdPartyLead = "NEW_THIRD_PARTY_LEAD",
	IncomingSmsNotification = "SMS_RECEIVED",
	QuoteCommentCreated = "QUOTE_COMMENT_CREATED",
	QuoteStatusChanged = "QUOTE_STATUS_CHANGED",
	UserMentioned = "USER_MENTIONED",
	JobUserMentioned = "JOB_USER_MENTIONED", // TODO: discuss with backend
	JobNote = "JOB_NOTE_REMINDER",
	JobStatus = "JOB_STATUS",
	JobBDPAssignee = "JOB_BUSINESS_DEVELOPMENT_PARTNER_ASSIGNED",
	JobAgentAssignee = "JOB_AGENT_ASSIGNED",
	JobWatchers = "JOB_WATCHERS",
	JobCreatedFromQuote = "JOB_CREATED_FROM_QUOTE",
	InvoiceCreated = "INVOICE_CREATED",
	BillCreated = "INVOICE_IN_XERO_CREATED",
	PaymentArrived = "PAYMENT_ARRIVED",
	InvoicePaid = "INVOICE_PAID",
	BillPaid = "BILL_PAID"
}

/**
 * Notification type.
 */
export type NotificationType =
	ReminderNotification
	| LeadStatusNotification
	| AssigneeChangeNotification
	| LeadBDPChangeNotification
	| LeadAgentChangeNotification
	| LeadWatcherChangeNotification
	| SmsNotification
	| QuoteCommentNotification
	| QuoteStatusChangedNotification
	| UserMentionNotification
	| JobUserMentionNotification
	| JobReminderNotification
	| JobStatusNotification
	| JobBDPChangeNotification
	| JobAgentChangeNotification
	| JobWatcherChangeNotification
	| JobCreatedFromQuoteNotification
	| NewThirdPartyLeadNotification
	| InvoiceCreatedNotification
	| BillCreatedNotification
	| PaymentArrivedNotification
	| BillPaidNotification;
